import {
  Tone,
  type SizeMap,
  type ToneMap,
  type Size,
} from '@gokrew/models/ui.types';

/**
 * Chip Component Props
 */
export interface ChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  type?: Tone;
  size?: Size;
  children: React.ReactNode;
  showDot?: boolean;
  dotTone?: Tone;
}

/** Mapping of size variants to their corresponding Tailwind CSS classes. */
const sizeMap: SizeMap<string> = {
  xs: 'px-1 py-0.5 text-[8px] rounded-full',
  sm: 'px-2 py-0.5 text-[10px] rounded-full',
  md: 'px-2 py-1 text-xs rounded-[8px]',
  lg: 'px-3 py-1.5 text-sm rounded-[8px]',
};

/** Mapping of tone variants to their corresponding Tailwind CSS classes. */
const toneMap: ToneMap<string> = {
  primary: 'bg-blue-100 text-blue-800',
  secondary: 'bg-gray-100 text-gray-800',
  success: 'bg-green-100 text-green-800',
  warning: 'bg-amber-100 text-amber-900',
  error: 'bg-red-100 text-red-800',
  neutral: 'text-gray-700 bg-white border border-gray-300',
  accent: 'bg-violet-50 text-violet-700',
  softBlue: 'bg-[#DAE2FD] text-blue-800',
  softGray: 'bg-[#F2F4F6] text-gray-700 border border-[#E5E9EE]',
};

const getDotColor = (type: Tone) => {
  switch (type) {
    case Tone.SUCCESS:
      return 'bg-green-500';
    case Tone.ERROR:
      return 'bg-red-500';
    case Tone.WARNING:
      return 'bg-amber-500';
    case Tone.PRIMARY:
      return 'bg-blue-500';
    case Tone.ACCENT:
      return 'bg-violet-500';
    case Tone.SOFT_BLUE:
      return 'bg-blue-400';
    default:
      return 'bg-gray-400';
  }
};

/**
 * Chip component
 *
 * @description
 * A small, rounded rectangle with a label or icon. Used for representing
 * a person, place, or thing in a compact way.
 *
 * @prop {string} type - The tone of the chip. One of "primary", "secondary", "success", "warning", or "error".
 * @prop {Size} size - The size of the chip. One of "sm", "md", or "lg".
 * @prop {React.ReactNode} children - The contents of the chip.
 */
export function Chip({
  type = Tone.PRIMARY,
  size = 'md',
  children,
  className,
  showDot = false,
  dotTone,
  ...props
}: ChipProps) {
  return (
    <span
      {...props}
      className={`inline-flex max-w-full items-center gap-1.5 overflow-hidden ${sizeMap[size]} ${toneMap[type]} ${className ?? ''}`}
    >
      {showDot && (
        <span
          className={`
            w-1.5 h-1.5 rounded-full shrink-0
            ${getDotColor(dotTone ?? type)}
          `}
        />
      )}

      {children}
    </span>
  );
}
